export const RESTAURANT_HOURS = {
	timezone: "Europe/Berlin",

	// Hours in 24-hour format (HH:MM)
	weekday: {
		open: "15:00", // 3:00 PM
		close: "23:00", // 11:00 PM
	},

	weekend: {
		open: "13:00", // 1:00 PM
		close: "23:00", // 11:00 PM
	},

	// Manual override (can be set to true to close restaurant temporarily)
	isClosed: false,
} as const;

export type RestaurantHours = typeof RESTAURANT_HOURS;

/**
 * Check if the restaurant is currently open
 */
export function isRestaurantOpen(now: Date = new Date()): boolean {
	if (RESTAURANT_HOURS.isClosed) return false;

	const dayOfWeek = now.getDay(); // 0 = Sunday, 6 = Saturday
	const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

	const hours = isWeekend ? RESTAURANT_HOURS.weekend : RESTAURANT_HOURS.weekday;
	const [openHour, openMin] = hours.open.split(":").map(Number);
	const [closeHour, closeMin] = hours.close.split(":").map(Number);

	const currentHour = now.getHours();
	const currentMin = now.getMinutes();
	const currentTime = currentHour * 60 + currentMin;
	const openTime = openHour * 60 + openMin;
	const closeTime = closeHour * 60 + closeMin;

	return currentTime >= openTime && currentTime < closeTime;
}

/**
 * Get the next opening time if restaurant is closed
 */
export function getNextOpenTime(now: Date = new Date()): Date | undefined {
	if (RESTAURANT_HOURS.isClosed) return undefined;

	const dayOfWeek = now.getDay();
	const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
	const hours = isWeekend ? RESTAURANT_HOURS.weekend : RESTAURANT_HOURS.weekday;

	const [openHour, openMin] = hours.open.split(":").map(Number);

	// If we're before opening time today, return today's opening time
	const todayOpenTime = new Date(now);
	todayOpenTime.setHours(openHour, openMin, 0, 0);

	if (now < todayOpenTime) {
		return todayOpenTime;
	}

	// If we're after closing time, return tomorrow's opening time
	const tomorrow = new Date(now);
	tomorrow.setDate(tomorrow.getDate() + 1);
	const tomorrowOpenTime = new Date(tomorrow);
	tomorrowOpenTime.setHours(openHour, openMin, 0, 0);

	return tomorrowOpenTime;
}

/**
 * Check if a given time is during restaurant hours
 */
export function isTimeDuringHours(dateTime: Date): boolean {
	if (RESTAURANT_HOURS.isClosed) return false;

	const dayOfWeek = dateTime.getDay();
	const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
	const hours = isWeekend ? RESTAURANT_HOURS.weekend : RESTAURANT_HOURS.weekday;

	const [openHour, openMin] = hours.open.split(":").map(Number);
	const [closeHour, closeMin] = hours.close.split(":").map(Number);

	const timeHour = dateTime.getHours();
	const timeMin = dateTime.getMinutes();
	const timeInMinutes = timeHour * 60 + timeMin;
	const openTime = openHour * 60 + openMin;
	const closeTime = closeHour * 60 + closeMin;

	return timeInMinutes >= openTime && timeInMinutes < closeTime;
}
